import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, CheckCircle, FileText, Video, Clapperboard, Bell, FileWarning, Scissors, Film } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { StatoContenuto } from '../types';
import { Card, CardHeader, CardTitle } from '../components/ui/Card';
import { SuccessNotification, ProgressAchievement } from '../components/SuccessStates';
import { useSchedeClienti } from '../hooks/useSchedeClienti';
import ContatoriProfili from '../components/ContatoriProfili';
import ContatorePost from '../components/ContatorePost';

const Dashboard: React.FC = () => {
  const { state } = useData();
  const navigate = useNavigate();
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showProgressAchievement, setShowProgressAchievement] = useState(false);
  const [celebrationType, setCelebrationType] = useState<'milestone_reached' | 'goal_completed' | 'monthly_target'>('milestone_reached');

  // Schede per cliente, avvisi e contatori: calcolo unico condiviso con
  // "Maggiori dettagli" (hooks/useSchedeClienti.ts). Carica anche stats e
  // appuntamenti; gli avvisi compaiono solo a dati pronti, per non lampeggiare.
  const { schede: schedeClienti, alertDataPronti, profiliRiprese, profiliTesti, profiliDaProgrammare } = useSchedeClienti();

  const stats = state.dashboardStats;
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const reelDaRealizzare = stats ? stats.monthly_reels_target : state.clienti.filter(cliente => cliente.status === 'active').reduce((total, cliente) => total + (cliente.reelMensili || 0), 0);

  const reelRealizzati = stats ? stats.reels_completed : (() => {
    const contenutiCompletati = state.contenuti.filter(contenuto => {
      if (!contenuto.dataPubblicazione) return false;
      const dataPub = new Date(contenuto.dataPubblicazione);
      const isQuestoMese = dataPub.getMonth() === currentMonth && dataPub.getFullYear() === currentYear;
      return isQuestoMese && (
        contenuto.stato === StatoContenuto.PROGRAMMATO ||
        contenuto.stato === StatoContenuto.PUBBLICATO ||
        contenuto.stato === StatoContenuto.ARCHIVIATO
      );
    });
    return contenutiCompletati.length;
  })();

  const percentualeCompletamento = stats ? Math.round(stats.completion_percentage) : (reelDaRealizzare > 0 ? Math.round((reelRealizzati / reelDaRealizzare) * 100) : 0);

  useEffect(() => {
    const checkAchievements = () => {
      if (percentualeCompletamento >= 100 && reelDaRealizzare > 0) {
        setCelebrationType('monthly_target');
        setShowSuccessNotification(true);
        return;
      }
      if (percentualeCompletamento >= 75 && percentualeCompletamento < 100) {
        setShowProgressAchievement(true);
      }
    };
    if (reelDaRealizzare > 0) {
      checkAchievements();
    }
  }, [percentualeCompletamento, reelDaRealizzare]);

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Header */}
      <div className="mb-2">
        <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>
          {(() => { const m = new Date().toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }); return m.charAt(0).toUpperCase() + m.slice(1); })()}
        </h1>
      </div>

      {/* Clienti Attivi */}
      <Card>
        <CardHeader className="!flex-col !items-start">
          <CardTitle className="text-base sm:text-lg">Clienti Attivi</CardTitle>
          {/* Sotto il titolo: quanti profili hanno un avviso aperto (stessi tre
              avvisi delle card). Colori delle righe corrispondenti: viola = da
              registrare, giallo = da scrivere, verde = programmati */}
          {alertDataPronti && (
            <ContatoriProfili riprese={profiliRiprese} testi={profiliTesti} daProgrammare={profiliDaProgrammare} />
          )}
        </CardHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
          {schedeClienti.map(({
            cliente, clienteId, testiDalCliente, daScrivere, daRegistrare,
            registratiNonProgrammati, daMontare: daMontareCliente, montati: montatiCliente,
            programmati: completatiCliente, reelMensili: reelMensiliCliente, progresso: clientProgress, obiettivoRaggiunto: isClientComplete,
            mancantiAlTarget, reelGiaGirati, attesoAdOggi, occorronoRiprese, mancanoTesti, daProgrammareOra,
          }) => {
            return (
              <div
                key={cliente.id}
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/copywriter?client=${clienteId}`)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/copywriter?client=${clienteId}`); } }}
                className={`block cursor-pointer p-3 sm:p-4 rounded-xl border transition-all duration-200 hover:shadow-md bg-white dark:bg-gray-800 ${
                  isClientComplete
                    ? 'border-emerald-400/70 dark:border-emerald-500/50 ring-1 ring-emerald-400/15 hover:border-emerald-400 dark:hover:border-emerald-500'
                    : 'border-slate-200 dark:border-gray-700 hover:border-slate-300 dark:hover:border-gray-600'
                }`}
              >
                {alertDataPronti && occorronoRiprese && (
                  <div
                    className="flex items-center gap-1 mb-2 text-[9px] sm:text-[10px] font-semibold text-red-500 dark:text-red-400"
                    title={`Ritmo mensile in ritardo: attesi ~${attesoAdOggi} reel girati entro oggi, disponibili ${reelGiaGirati}. Sollecita le riprese al videomaker.`}
                  >
                    <Clapperboard className="w-2.5 h-2.5 flex-shrink-0" /> Occorrono subito delle riprese
                  </div>
                )}
                {alertDataPronti && mancanoTesti && (
                  <div
                    className="flex items-center gap-1 mb-2 text-[9px] sm:text-[10px] font-semibold text-orange-500 dark:text-orange-400"
                    title="Le riprese sono fissate sul calendario del videomaker, ma non ci sono testi pronti da registrare: il copywriter deve ancora scrivere gli script."
                  >
                    <FileWarning className="w-2.5 h-2.5 flex-shrink-0" /> Ci sono già le riprese, mancano i testi!
                  </div>
                )}
                {alertDataPronti && daProgrammareOra && (
                  <div
                    className="flex items-center gap-1 mb-2 text-[9px] sm:text-[10px] font-semibold text-amber-600 dark:text-[#fdf751]"
                    title={`Hai ${registratiNonProgrammati} reel già registrato: programmane ${mancantiAlTarget} e completi il mese (target ${reelMensiliCliente})`}
                  >
                    <Bell className="w-2.5 h-2.5 flex-shrink-0" /> Contenuti Ok, programmali ora!
                  </div>
                )}
                <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                  {/* Logo cliente */}
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-slate-100 dark:bg-gray-700 flex-shrink-0 overflow-hidden">
                    {cliente.logoUrl ? (
                      <img
                        src={cliente.logoUrl}
                        alt={cliente.nomeAzienda}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400 dark:text-slate-500 text-xs sm:text-sm font-medium">
                        {cliente.nomeAzienda?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-slate-900 dark:text-slate-100 text-xs sm:text-sm truncate">
                      {cliente.nomeAzienda}
                    </h4>
                    <div className="flex items-center gap-1 mt-0.5 text-[10px] sm:text-xs text-slate-500 dark:text-slate-400">
                      <Target className="w-3 h-3 flex-shrink-0" />
                      <span><span className="font-semibold text-slate-700 dark:text-slate-300">{reelMensiliCliente}</span> reel previsti</span>
                    </div>
                    {/* Post grafici e caroselli foto del mese, come nel Copywriting (03/11/2026) */}
                    <ContatorePost cliente={cliente} contenuti={state.contenuti} mese={currentMonth} anno={currentYear} />
                  </div>
                  {isClientComplete && (
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                  )}
                </div>

                {/* Progress bar (programmati / previsti) */}
                <div className="w-full h-0.5 sm:h-1 bg-slate-200 dark:bg-gray-700 rounded-full overflow-hidden mb-2.5 sm:mb-3">
                  <div
                    className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-emerald-300/55 to-emerald-400/55"
                    style={{ width: `${Math.min(clientProgress, 100)}%` }}
                  />
                </div>

                {/* Metriche reel — righe con i colori dei tag */}
                <div className="space-y-1.5 sm:space-y-2">
                  {/* Da scrivere — giallo. Se i testi li scrive il cliente il
                      lato testo è già coperto: nessun numero, riga verde. */}
                  {testiDalCliente ? (
                    <div
                      className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/60 dark:border-emerald-800/40 px-2.5 py-1.5 flex items-center justify-between"
                      title="I testi li scrive il cliente: non c'è nulla da scrivere da parte nostra"
                    >
                      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-emerald-700 dark:text-emerald-400">
                        <FileText className="w-3.5 h-3.5 flex-shrink-0" /> Testi dal cliente
                      </div>
                      <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    </div>
                  ) : (
                    <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-800/40 px-2.5 py-1.5 flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-amber-700 dark:text-amber-400">
                        <FileText className="w-3.5 h-3.5 flex-shrink-0" /> Da scrivere
                      </div>
                      <div className="text-base sm:text-lg font-bold text-amber-600 dark:text-amber-400 leading-none">{daScrivere}</div>
                    </div>
                  )}
                  {/* Da registrare — viola (da girare col cliente, come in Videomaker) */}
                  <div className="rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200/60 dark:border-violet-800/40 px-2.5 py-1.5 flex items-center justify-between" title="Approvati (testo pronto) e ancora da registrare col cliente — come nella pagina Videomaker">
                    <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-violet-700 dark:text-violet-400">
                      <Clapperboard className="w-3.5 h-3.5 flex-shrink-0" /> Da registrare
                    </div>
                    <div className="text-base sm:text-lg font-bold text-violet-600 dark:text-violet-400 leading-none">{daRegistrare}</div>
                  </div>
                  {/* Registrati — blu: reel girati nel mese, programmati o meno.
                      Stesso numero del badge "Registrati" della pagina Videomaker
                      (reelGiratiNelMese in utils/ripreseMatch.ts). */}
                  <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200/60 dark:border-blue-800/40 px-2.5 py-1.5 flex items-center justify-between" title={`Reel registrati questo mese dal videomaker, anche se già programmati (${registratiNonProgrammati} ancora da programmare)`}>
                    <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-blue-700 dark:text-blue-400">
                      <Video className="w-3.5 h-3.5 flex-shrink-0" /> Registrati
                    </div>
                    <div className="text-base sm:text-lg font-bold text-blue-600 dark:text-blue-400 leading-none">{reelGiaGirati}</div>
                  </div>
                  {/* Da montare — arancione: registrati in attesa del montaggio
                      (pagina Editing), anche se già programmati */}
                  <div className="rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200/60 dark:border-orange-800/40 px-2.5 py-1.5 flex items-center justify-between" title="Registrati e non ancora montati, anche se già programmati — come nella pagina Editing">
                    <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-orange-700 dark:text-orange-400">
                      <Scissors className="w-3.5 h-3.5 flex-shrink-0" /> Da montare
                    </div>
                    <div className="text-base sm:text-lg font-bold text-orange-600 dark:text-orange-400 leading-none">{daMontareCliente}</div>
                  </div>
                  {/* Montati — verde acqua: montati nel mese, programmati o meno */}
                  <div className="rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-200/60 dark:border-teal-800/40 px-2.5 py-1.5 flex items-center justify-between" title="Video montati questo mese, anche se già programmati — come nella pagina Editing">
                    <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-teal-700 dark:text-teal-400">
                      <Film className="w-3.5 h-3.5 flex-shrink-0" /> Montati
                    </div>
                    <div className="text-base sm:text-lg font-bold text-teal-600 dark:text-teal-400 leading-none">{montatiCliente}</div>
                  </div>
                  {/* Programmati — verde */}
                  <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/60 dark:border-emerald-800/40 px-2.5 py-1.5 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-emerald-700 dark:text-emerald-400 min-w-0">
                      <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">Programmati</span>
                    </div>
                    <div className="text-base sm:text-lg font-bold text-emerald-600 dark:text-emerald-400 leading-none flex-shrink-0">{completatiCliente}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Success Notifications */}
      {showSuccessNotification && (
        <SuccessNotification
          type={celebrationType}
          onClose={() => setShowSuccessNotification(false)}
          data={{ monthlyTarget: reelDaRealizzare, completed: reelRealizzati }}
        />
      )}

      <ProgressAchievement
        show={showProgressAchievement}
        progress={reelRealizzati}
        target={reelDaRealizzare}
        title="Progresso Mensile"
        onClose={() => setShowProgressAchievement(false)}
      />
    </div>
  );
};

export default Dashboard;
